import { EventBus } from "../../contexts";
import { ACTIVE_SMART_LABELS, SmartLabel } from "../mail/types/mail";
import { SmartLabelEntry } from "./types/customViews";

export class SmartLabelStore {
    private eventBus: EventBus;

    /** Store interne des SmartLabels — clé = valeur de l'enum SmartLabel, trié par le backend */
    public entries: Partial<Record<SmartLabel, SmartLabelEntry[]>> = {};

    constructor(eventBus: EventBus) {
        this.eventBus = eventBus;
    }

    /**
     * (#1672 — fix ④ RaceRE-03) Capture un instantané (id → entrée) des entrées du store pour les ids
     * fournis, à un instant t. Utilisé par la réconciliation (`MailManager._reconcileSmartLabelStoreCounters`)
     * AVANT son appel async `verifyMailsExistence` : après coup, on ne retirera un id que si son entrée n'a
     * PAS changé de référence entre-temps (cf. `_removeMessageFromSmartLabelStoreIfUnchanged`).
     */
    public snapshotEntriesById(
        internetMessageIds: string[],
    ): Map<string, SmartLabelEntry> {
        const wanted = new Set(internetMessageIds);
        const snapshot = new Map<string, SmartLabelEntry>();
        for (const label of ACTIVE_SMART_LABELS) {
            for (const entry of this.entries[label] ?? []) {
                if (wanted.has(entry.id)) snapshot.set(entry.id, entry);
            }
        }
        return snapshot;
    }

    /**
     * (#1672 — fix ④ RaceRE-03) Retrait « anti-verdict-périmé ». Retire l'id du store SEULEMENT si son
     * entrée courante est TOUJOURS celle capturée dans le snapshot (même référence). Si le mail a été
     * ré-affirmé pendant le trou async de la réconciliation (`_updateSmartLabelEntryAndSendEvent` recrée
     * une nouvelle entrée), la référence diffère → on N'APPLIQUE PAS le verdict (le mail a été restauré).
     * @returns true si le retrait a eu lieu, false s'il a été ignoré (entrée changée/absente).
     */
    public removeMessageIfUnchanged(
        internetMessageId: string,
        snapshotEntry: SmartLabelEntry | undefined,
    ): boolean {
        let current: SmartLabelEntry | undefined;
        for (const label of ACTIVE_SMART_LABELS) {
            const found = (this.entries[label] ?? []).find(
                (e) => e.id === internetMessageId,
            );
            if (found) {
                current = found;
                break;
            }
        }

        if (current === undefined || current !== snapshotEntry) {
            return false; // déjà absent, ou ré-affirmé pendant l'appel async → verdict périmé, on ignore
        }

        this.removeMessage(internetMessageId);
        return true;
    }

    /**
     * (Fix C — AB#1645) Union dédupliquée des internetMessageIds présents dans le store SmartLabel,
     * tous labels confondus. Les SmartLabels sont mutuellement exclusifs (un mail ne porte qu'un label),
     * mais on déduplique par sécurité. Utilisé par la réconciliation de fin de Phase 4
     * (`MailManager._reconcileSmartLabelStore`) : son coût est ∝ taille du store, indépendant de l'inbox.
     */
    public getAllInternetMessageIds(): string[] {
        const allIds = new Set<string>();
        for (const label of ACTIVE_SMART_LABELS) {
            for (const entry of this.entries[label] ?? []) {
                allIds.add(entry.id);
            }
        }
        return [...allIds];
    }

    public removeMessage(internetMessageId: string): void {
        for (const label of ACTIVE_SMART_LABELS) {
            const entries = this.entries[label];
            if (!entries) continue;

            const filtered = entries.filter((e) => e.id !== internetMessageId);
            if (filtered.length !== entries.length) {
                this.entries[label] = filtered;
                this.eventBus.emit("custom-view-list-updated", {
                    customViewId: label,
                });
                this.eventBus.emit("navigation-asking-for-update");
            }
        }
    }
}
